import React, { useState, useEffect } from 'react';
import { fetchAddressByCode, fetchAddressesByUserId, deleteAddress, updateAddress } from '../services/addressService'; 
import { QRCodeCanvas } from 'qrcode.react'; 
import '../FetchAddress.css';

function FetchAddress() {
  const [searchValue, setSearchValue] = useState('');
  const [results, setResults] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [editAddress, setEditAddress] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [showQRCode, setShowQRCode] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('jwtToken');
    const userId = localStorage.getItem('userId');

    if (!token) {
      setErrorMessage('You need to log in first!');
      return;
    }

    const fetchAddresses = async () => {
      try {
        const response = await fetchAddressesByUserId(userId, token);
        setResults(response); 
      } catch (error) {
        if (error.response) {
          setErrorMessage('Error fetching addresses. Please try again later.');
        } else {
          setErrorMessage('An unknown error occurred while fetching addresses.');
        }
      }
    };

    fetchAddresses();
  }, []); 

  const handleInputChange = (e) => {
    setSearchValue(e.target.value);
  };

  const handleFetchByCode = async () => {
    setResults(null);
    setErrorMessage('');

    const token = localStorage.getItem('jwtToken');
    const userId = localStorage.getItem('userId');

    if (!token) {
      setErrorMessage('You need to log in first!');
      return;
    }

    try {
      const response = await fetchAddressByCode(searchValue, userId, token);
      setResults([response]);
    } catch (error) {
      if (error.response) {
        if (error.response.status === 400) {
          setErrorMessage('Invalid address code. Please check your search value.');
        } else if (error.response.status === 404) {
          setErrorMessage('Address not found.');
        } else {
          setErrorMessage(`Error: ${error.response.data.message || 'An unknown error occurred.'}`);
        }
      } else {
        setErrorMessage('An error occurred while fetching the address.');
      }
    }
  };

  const handleEdit = (address) => {
    setEditAddress(address);
  };

  const handleDelete = async (addressId) => {
    const token = localStorage.getItem('jwtToken');
    if (!token) {
      setErrorMessage('You need to log in first!');
      return;
    }

    if (window.confirm('Are you sure you want to delete this address?')) {
      try {
        await deleteAddress(addressId, token);
        setResults(results.filter((address) => address.addressId !== addressId)); 
        setPopupMessage('Address deleted successfully!');
        setShowPopup(true);
      } catch (error) {
        setErrorMessage('Error deleting the address. Please try again later.');
      }
    }
  };

  const handleUpdate = async () => {
    const token = localStorage.getItem('jwtToken');
    if (!token) {
      setErrorMessage('You need to log in first!');
      return;
    }

    try {
      await updateAddress(editAddress, token);
      setResults(results.map((address) => (address.addressId === editAddress.addressId ? editAddress : address)));
      setEditAddress(null);
      setPopupMessage('Address updated successfully!');
      setShowPopup(true);
    } catch (error) {
      setErrorMessage('Error updating the address. Please try again later.');
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditAddress({ ...editAddress, [name]: value });
  };

  const closePopup = () => {
    setShowPopup(false);
  };

  const handleCopyAndGenerateQRCode = (addressCode) => {
    navigator.clipboard.writeText(addressCode).then(() => {
      setPopupMessage(`Address Code "${addressCode}" copied to clipboard!`);
      setShowPopup(true);
    }).catch((err) => {
      console.error('Failed to copy address code:', err);
      setErrorMessage('Failed to copy address code to clipboard');
    });
  };

  const handleShowQRCode = (addressCode) => {
    setShowQRCode(addressCode); 
  };

  return (
    <div className="address-container mt-5">
      <h2 className="address-heading text-center mb-4">Fetch Address</h2>

      {/* Address Code Search Input */}
      <div className="address-search mb-3">
        <label htmlFor="searchValue" className="form-label">Enter Address Code:</label>
        <input
          type="text"
          className="form-control"
          id="searchValue"
          value={searchValue}
          onChange={handleInputChange}
          required
        />
      </div>

      {/* Fetch Button */}
      <div className="address-action text-center">
        <button className="btn btn-primary" onClick={handleFetchByCode}>Fetch by Address Code</button>
      </div>

      {/* Display Error Message */}
      {errorMessage && (
        <div className="alert alert-danger mt-3 error-message">
          <strong>Error:</strong> {errorMessage}
        </div>
      )}

      {/* Display Results */}
      {results && (
        <div className="address-results mt-4">
          <h4>Your saved Address:</h4>
          {results.length > 0 ? (
            <table className="table table-bordered mt-3 address-table">
              <thead>
                <tr>
                  <th>Address Code</th>
                  <th>Address Text</th>
                  <th>City</th>
                  <th>State</th>
                  <th>Zip Code</th>
                  <th>Country</th>
                  <th>Locality</th>
                  <th>Landmark</th>
                  <th>Alternate Phone</th>
                  <th>Address Type</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {results.map((address, index) => (
                  <tr key={index}>
                    <td>{address.addressCode}</td>
                    <td>{address.addressText}</td>
                    <td>{address.city}</td>
                    <td>{address.state}</td>
                    <td>{address.zipCode}</td>
                    <td>{address.country}</td>
                    <td>{address.locality}</td>
                    <td>{address.landmark}</td>
                    <td>{address.alternatePhone}</td>
                    <td>{address.addressType}</td>
                    <td className="action-buttons">
                      <button className="btn btn-warning me-2" onClick={() => handleEdit(address)}>Edit</button>
                      <button className="btn btn-danger" onClick={() => handleDelete(address.addressId)}>Delete</button>
                      <button className="btn btn-info me-2" onClick={() => handleCopyAndGenerateQRCode(address.addressCode)}>
                        Copy Address Code
                      </button>
                      <button className="btn btn-secondary" onClick={() => handleShowQRCode(address.addressCode)}>
                        Show QR Code
                      </button>

                      {/* Conditionally render QR Code */}
                      {showQRCode === address.addressCode && <QRCodeCanvas value={address.addressCode} size={100} />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>No records found.</p>
          )}
        </div>
      )}

      {/* Edit Form */}
      {editAddress && (
        <div className="address-edit mt-4">
          <h4>Edit Address</h4>
          <form onSubmit={(e) => { e.preventDefault(); handleUpdate(); }}>
            <div className="mb-3">
              <label htmlFor="addressText" className="form-label">Address Text:</label>
              <textarea
                className="form-control"
                id="addressText"
                name="addressText"
                value={editAddress.addressText}
                onChange={handleEditChange}
                required
              ></textarea>
            </div>
            <div className="mb-3">
              <label htmlFor="city" className="form-label">City:</label>
              <input
                type="text"
                className="form-control"
                id="city"
                name="city"
                value={editAddress.city}
                onChange={handleEditChange}
                required
              />
            </div>
            <div className="mb-3">
              <label htmlFor="state" className="form-label">State:</label>
              <input
                type="text"
                className="form-control"
                id="state"
                name="state"
                value={editAddress.state}
                onChange={handleEditChange}
                required
              />
            </div>
            <div className="mb-3">
              <label htmlFor="zipCode" className="form-label">Zip Code:</label>
              <input
                type="text"
                className="form-control"
                id="zipCode"
                name="zipCode"
                value={editAddress.zipCode}
                onChange={handleEditChange}
                required
              />
            </div>
            <div className="mb-3">
              <label htmlFor="country" className="form-label">Country:</label>
              <input
                type="text"
                className="form-control"
                id="country"
                name="country"
                value={editAddress.country}
                onChange={handleEditChange}
                required
              />
            </div>

            {/* New fields in the edit form */}
            <div className="mb-3">
              <label htmlFor="locality" className="form-label">Locality:</label>
              <input
                type="text"
                className="form-control"
                id="locality"
                name="locality"
                value={editAddress.locality}
                onChange={handleEditChange}
                required
              />
            </div>
            <div className="mb-3">
              <label htmlFor="landmark" className="form-label">Landmark:</label>
              <input
                type="text"
                className="form-control"
                id="landmark"
                name="landmark"
                value={editAddress.landmark}
                onChange={handleEditChange}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="alternatePhone" className="form-label">Alternate Phone:</label>
              <input
                type="text"
                className="form-control"
                id="alternatePhone"
                name="alternatePhone"
                value={editAddress.alternatePhone}
                onChange={handleEditChange}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="addressType" className="form-label">Address Type:</label>
              <select
                className="form-control"
                id="addressType"
                name="addressType"
                value={editAddress.addressType}
                onChange={handleEditChange}
              >
                <option value="Home">Home</option>
                <option value="Office">Office</option>
                <option value="Other">Other</option>
              </select>
            </div>
            
            <div className="text-center">
              <button type="submit" className="btn btn-success">Update Address</button>
            </div>
          </form>
        </div>
      )}

      {/* Show Popup */}
      {showPopup && (
        <div className="popup-overlay">
          <div className="popup-content">
            <p>{popupMessage}</p>
            <button onClick={closePopup} className="btn btn-primary">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default FetchAddress;
